import React, { useEffect, useRef, useState } from "react";
import {
  GoogleMap,
  Marker,
  StandaloneSearchBox,
  Circle,
  useJsApiLoader,
} from "@react-google-maps/api";

import "./LocationPicker.css";

const libraries = ["places"];

const containerStyle = {
  width: "100%",
  height: "100%",
};

const defaultCenter = {
  lat: 10.0735,
  lng: 78.7794,
};

export default function LocationPicker({
  open,
  onClose,
  onSelect,
  initialLocation,
  autoLocate = false,
}) {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries,
  });

  const mapRef = useRef(null);
  const searchRef = useRef(null);

  const [map, setMap] = useState(null);
  const [marker, setMarker] = useState(defaultCenter);
  const [zoom, setZoom] = useState(16);
  const [mapType, setMapType] = useState("roadmap");
  const [loading, setLoading] = useState(false);
  const [radius, setRadius] = useState(5000);

  const [location, setLocation] = useState({
    address: "",
    city: "",
    district: "",
    state: "",
    pincode: "",
    country: "India",
    latitude: defaultCenter.lat,
    longitude: defaultCenter.lng,
  });

  useEffect(() => {
    if (!initialLocation) return;

    if (
      typeof initialLocation.latitude === "number" &&
      typeof initialLocation.longitude === "number"
    ) {
      const pos = {
        lat: initialLocation.latitude,
        lng: initialLocation.longitude,
      };

      setMarker(pos);

      setLocation({
        address: initialLocation.address || "",
        city: initialLocation.city || "",
        district: initialLocation.district || "",
        state: initialLocation.state || "",
        pincode: initialLocation.pincode || "",
        country: initialLocation.country || "India",
        latitude: initialLocation.latitude,
        longitude: initialLocation.longitude,
      });

      if (map) {
        map.panTo(pos);
      }
    }
  }, [initialLocation, map]);

  useEffect(() => {
  if (open && autoLocate && map) {
    getCurrentLocation();
  }
}, [open, autoLocate, map]);

  if (!open) return null;

  const getComponent = (components, type) => {
    return (
      components.find((c) => c.types.includes(type))
        ?.long_name || ""
    );
  };

  const reverseGeocode = (lat, lng) => {
    if (!window.google) return;

    setLoading(true);

    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode(
      {
        location: { lat, lng },
      },
      (results, status) => {
        setLoading(false);

        if (status !== "OK" || !results || !results.length) return;

        const result = results[0];
        const components = result.address_components;

        // --- OPTIONAL SECURITY SANITIZATION ---
        // If you want to strip out precise details (premise/room/house number) 
        // to protect user identity in the raw text block, combine safe components:
        const route = getComponent(components, "route");
        const sublocality = getComponent(components, "sublocality_level_1");
        const locality = getComponent(components, "locality");
        const stateName = getComponent(components, "administrative_area_level_1");
        
        const cleanAddress = [route, sublocality, locality, stateName]
          .filter(Boolean)
          .join(", ") || result.formatted_address;

        setLocation({
          // Use 'cleanAddress' here if you want privacy, or 'result.formatted_address' for full precision
          address: cleanAddress, 

          city:
            locality ||
            getComponent(components, "sublocality_level_1") ||
            getComponent(components, "postal_town") ||
            getComponent(components, "administrative_area_level_3"),

          district: getComponent(components, "administrative_area_level_2"),
          state: stateName,
          pincode: getComponent(components, "postal_code"),
          country: getComponent(components, "country"),
          latitude: lat,
          longitude: lng,
        });
      }
    );
  };

  const onMapClick = (e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    setMarker({ lat, lng });
    reverseGeocode(lat, lng);
  };

  const onMarkerDragEnd = (e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    setMarker({ lat, lng });
    reverseGeocode(lat, lng);
  };

  const searchPlace = () => {
    const places = searchRef.current?.getPlaces();
    if (!places?.length) return;

    const place = places[0];
    if (!place.geometry) return;

    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();

    setMarker({ lat, lng });

    if (map) {
      map.panTo({ lat, lng });
      map.setZoom(18);
      setZoom(18);
    }

    reverseGeocode(lat, lng);
  };

  const getCurrentLocation = () => {
  if (!navigator.geolocation) {
    alert("Geolocation is not supported.");
    return;
  }

  navigator.geolocation.getCurrentPosition(
    ({ coords }) => {
      const lat = coords.latitude;
      const lng = coords.longitude;

      const pos = { lat, lng };

      setMarker(pos);

      if (map) {
        map.panTo(pos);
        map.setZoom(18);
        setZoom(18);
      }

      reverseGeocode(lat, lng);
    },
    () => {
      alert("Unable to get current location.");
    },
    {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0,
    }
  );
};



  const resetLocation = () => {
    setMarker(defaultCenter);

    if (map) {
      map.panTo(defaultCenter);
      map.setZoom(15);
      setZoom(15);
    }

    reverseGeocode(defaultCenter.lat, defaultCenter.lng);
  };

  const zoomIn = () => {
    if (!map) return;
    const z = map.getZoom() + 1;
    map.setZoom(z);
    setZoom(z);
  };

  const zoomOut = () => {
    if (!map) return;
    const z = map.getZoom() - 1;
    map.setZoom(z);
    setZoom(z);
  };

  const copyCoordinates = () => {
    navigator.clipboard.writeText(
      `${location.latitude}, ${location.longitude}`
    );
    alert("Coordinates copied.");
  };

  const openFullscreen = () => {
    if (!map) return;
    const div = map.getDiv();
    if (div.requestFullscreen) {
      div.requestFullscreen();
    }
  };

  const mapOptions = {
    streetViewControl: false,
    fullscreenControl: false,
    mapTypeControl: false,
    zoomControl: false,
    rotateControl: false,
    clickableIcons: false,
    keyboardShortcuts: true,
    gestureHandling: "greedy",
  };

  const circleOptions = {
    fillColor: "#2563eb",
    fillOpacity: 0.15,
    strokeColor: "#2563eb",
    strokeOpacity: 0.8,
    strokeWeight: 2,
    clickable: false,
    editable: false,
    draggable: false,
  };

  return (
    <div className="vendor-modal-overlay">
      <div className="location-picker-modal">
        {/* ================= HEADER ================= */}
        <div className="location-picker-header">
          <div>
            <h3>Select Business Location</h3>
            <p className="location-subtitle">
              Search or tap on the map to pin your business location.
            </p>
          </div>
          <button className="close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        {/* ================= BODY ================= */}
        <div className="location-picker-body">
          {/* ================= SEARCH ================= */}
          {isLoaded && (
            <div className="search-wrapper">
              <StandaloneSearchBox
                onLoad={(ref) => (searchRef.current = ref)}
                onPlacesChanged={searchPlace}
              >
                <input
                  className="location-search"
                  placeholder="Search location..."
                />
              </StandaloneSearchBox>
            </div>
          )}

          {/* ================= TOOLBAR ================= */}
          <div className="map-toolbar">
            <button onClick={getCurrentLocation}>📍 Current</button>
            <button
              className={mapType === "roadmap" ? "active" : ""}
              onClick={() => setMapType("roadmap")}
            >
              🗺 Map
            </button>
            <button
              className={mapType === "satellite" ? "active" : ""}
              onClick={() => setMapType("satellite")}
            >
              🛰 Satellite
            </button>
            <button onClick={resetLocation}>Reset</button>
          </div>

          {/* ================= MAP ================= */}
          <div className="map-wrapper">
            {loading && <div className="map-loading">Fetching Address...</div>}

            {isLoaded && (
              <GoogleMap
                mapContainerStyle={containerStyle}
                center={marker}
                zoom={zoom}
                mapTypeId={mapType}
                options={mapOptions}
                onLoad={(m) => setMap(m)} // Cleaned up dev console logs here
                onClick={onMapClick}
                onZoomChanged={() => {
                  if (map) setZoom(map.getZoom());
                }}
              >
                <Marker
                  position={marker}
                  draggable
                  onDragEnd={onMarkerDragEnd}
                />
                <Circle
                  center={marker}
                  radius={radius}
                  options={circleOptions}
                />
              </GoogleMap>
            )}

            {/* ================= ZOOM ================= */}
            <div className="zoom-controls">
              <button onClick={zoomIn}>+</button>
              <button onClick={zoomOut}>−</button>
            </div>

            {/* ================= FULLSCREEN ================= */}
            <button className="fullscreen-btn" onClick={openFullscreen}>
              ⛶
            </button>
          </div>

          {/* ================= DELIVERY RADIUS ================= */}
          <div className="radius-section">
            <label>
              Delivery Radius
              <strong> ({radius / 1000} km)</strong>
            </label>
            <input
              type="range"
              min={1000}
              max={25000}
              step={1000}
              value={radius}
              onChange={(e) => setRadius(Number(e.target.value))}
            />
          </div>

          {/* ================= ADDRESS ================= */}
          <div className="address-card">
            <h4>Selected Address</h4>
            <textarea
              value={location.address}
              readOnly
              rows={4}
              placeholder="Tap on the map or search for a location..."
            />

            <div className="address-grid">
              <div>
                <label>City</label>
                <span>{location.city || "-"}</span>
              </div>
              <div>
                <label>District</label>
                <span>{location.district || "-"}</span>
              </div>
              <div>
                <label>State</label>
                <span>{location.state || "-"}</span>
              </div>
              <div>
                <label>Pincode</label>
                <span>{location.pincode || "-"}</span>
              </div>
              <div>
                <label>Country</label>
                <span>{location.country || "-"}</span>
              </div>
            </div>
          </div>

          {/* ================= COORDINATES ================= */}
          <div className="coordinates-card">
            <div>
              <label>Latitude</label>
              <span>
                {location.latitude ? Number(location.latitude).toFixed(6) : "-"}
              </span>
            </div>
            <div>
              <label>Longitude</label>
              <span>
                {location.longitude ? Number(location.longitude).toFixed(6) : "-"}
              </span>
            </div>
            <button className="copy-btn" onClick={copyCoordinates}>
              Copy Coordinates
            </button>
          </div>
        </div>

        {/* ================= FOOTER ================= */}
        <div className="location-footer">
          <button className="cancel-btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="admin-primary-btn"
            onClick={() => {
              onSelect({
                ...location,
                geo: {
                  type: "Point",
                  coordinates: [location.longitude, location.latitude],
                },
                deliveryRadius: radius,
              });
              onClose();
            }}
          >
            Confirm Location
          </button>
        </div>
      </div>
    </div>
  );
}